import React, { useCallback, useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, Box, TextField, Typography } from '@mui/material';
import InputGroupV2 from '../InputGroupV2';
import BaseService from 'services/BaseService';
import { useAutocompleteTabSelect } from '../autocompleteTabSelect';
import { useAutocompleteListSearch } from '../useAutocompleteListSearch';
import { normalizeText } from '../selectSearchFilter';
import { formFieldRootSx, autocompleteMultiInputSx, autocompleteMultiChipSlotProps } from '../formFieldStyles';
import {
  matchesEquipamentoQuery,
  scoreEquipamentoMatch,
} from './equipamentoSelectFilter';

const getLabel = (option) => {
  if (!option) return '';
  return option.modelo;
};

const EquipamentoSelectV2 = ({
  onSelect,
  value,
  isRequired,
  error,
  label,
  disabled,
  multiSelect,
  tipoEquipamento,
  filterUsuarioUltimoRepasse,
  showUsuarioTodosRepasse,
}) => {
  const [equipamentos, setEquipamentos] = useState([]);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      if (!tipoEquipamento) {
        setEquipamentos([]);
        return;
      }
      try {
        const response = await BaseService.get({
          url: `equipamentos/${tipoEquipamento}/options?filterUsuarioUltimoRepasse=${filterUsuarioUltimoRepasse ?? 'false'}&showUsuarioTodosRepasse=${showUsuarioTodosRepasse ?? 'false'}`,
        });
        if (!cancelled) {
          setEquipamentos(response?.data ?? []);
        }
      } catch (e) {
        console.error('Erro ao buscar equipamentos:', e);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [tipoEquipamento, filterUsuarioUltimoRepasse, showUsuarioTodosRepasse]);

  const getSingleValue = () => {
    if (value && equipamentos && equipamentos.length > 0) {
      const found = equipamentos.filter((e) => e.id === value);
      if (found.length > 0) {
        return found[0];
      }
    }
    return null;
  };

  const multiValue = useMemo(() => {
    if (!multiSelect || !value || !equipamentos.length) {
      return [];
    }
    return equipamentos.filter((e) => value.includes(e.id));
  }, [multiSelect, value, equipamentos]);

  const singleValue = getSingleValue();
  const matchFn = useCallback((equipamento, q) => matchesEquipamentoQuery(equipamento, q), []);
  const sortFn = useCallback((a, b, q) => {
    const scoreDiff = scoreEquipamentoMatch(b, q) - scoreEquipamentoMatch(a, q);
    if (scoreDiff !== 0) {
      return scoreDiff;
    }
    return normalizeText(a.modelo).localeCompare(normalizeText(b.modelo), 'pt-BR');
  }, []);

  const {
    inputValue,
    onInputChange,
    onClose,
    filterOptions,
    listboxSlotProps,
    noOptionsText,
  } = useAutocompleteListSearch({
    optionsCount: equipamentos.length,
    selectedLabel: getLabel(singleValue),
    multiSelect,
    matchFn,
    sortFn,
    getSortLabel: getLabel,
  });

  const handleFilterOptions = useCallback(
    (options) => filterOptions(options),
    [filterOptions]
  );

  const applyTabSelection = useCallback(
    (_event, option) => {
      if (!option) return;
      if (multiSelect) {
        const ids = Array.isArray(value) ? value : [];
        if (!ids.includes(option.id)) {
          onSelect([...ids, option.id]);
        }
      } else {
        onSelect(option.id, getLabel(option));
      }
    },
    [multiSelect, value, onSelect]
  );

  const { onHighlightChange, onKeyDown: onTabKeyDown } = useAutocompleteTabSelect(applyTabSelection);

  return (
    <InputGroupV2 label={label} isRequired={isRequired}>
      <Autocomplete
        disabled={disabled || !tipoEquipamento}
        disableClearable={!multiSelect}
        filterOptions={handleFilterOptions}
        getOptionLabel={getLabel}
        inputValue={inputValue}
        isOptionEqualToValue={(a, b) => a && b && a.id === b.id}
        multiple={multiSelect}
        noOptionsText={noOptionsText}
        options={equipamentos}
        value={multiSelect ? multiValue : singleValue}
        onChange={(event, newValue) => {
          if (multiSelect) {
            onSelect(newValue && newValue.length ? newValue.map((e) => e.id) : []);
          } else if (newValue) {
            onSelect(newValue.id, getLabel(newValue));
          } else {
            onSelect(null);
          }
        }}
        onClose={onClose}
        onHighlightChange={onHighlightChange}
        onInputChange={onInputChange}
        onKeyDown={onTabKeyDown}
        renderInput={(params) => (
          <TextField
            {...params}
            error={error}
            fullWidth
            placeholder="Selecione..."
            sx={
              multiSelect
                ? autocompleteMultiInputSx(disabled)
                : {
                  '& .MuiInputBase-root': formFieldRootSx(disabled),
                }
            }
          />
        )}
        renderOption={(props, option, { selected }) => (
          <li {...props}>
            <Box
              sx={{
                bgcolor: selected ? 'primary.main' : 'transparent',
                color: selected ? 'primary.contrastText' : 'text.primary',
                cursor: 'default',
                display: 'block',
                fontSize: 'inherit',
                py: 1,
                px: 1.5,
                userSelect: 'none',
                width: '100%',
              }}
            >
              <Typography
                variant="body2"
                sx={{ color: selected ? 'primary.contrastText' : 'text.primary' }}
              >
                {getLabel(option)}
              </Typography>
              <Typography variant="caption" component="div">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Série:{' '}
                </Box>
                {option.numSerie ?? ''}
              </Typography>
              <Typography variant="caption" component="div">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Patrimônio:{' '}
                </Box>
                {option.numPatrimonio ?? ''}
              </Typography>
              <Typography variant="caption" component="div">
                <Box component="span" sx={{ fontWeight: 'bold' }}>
                  Código SISANT:{' '}
                </Box>
                {option.codigoSisant ?? ''}
              </Typography>
            </Box>
          </li>
        )}
        slotProps={{
          chip: autocompleteMultiChipSlotProps,
          listbox: listboxSlotProps,
          popper: {
            sx: (theme) => ({
              zIndex: theme.zIndex.modal + 1,
            }),
          },
        }}
      />
      {error && (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', mt: 1, ml: 1.5, display: 'block' }}
        >
          O campo é obrigatório
        </Typography>
      )}
    </InputGroupV2>
  );
};

EquipamentoSelectV2.propTypes = {
  error: PropTypes.bool,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  isRequired: PropTypes.any,
  onSelect: PropTypes.func.isRequired,
  label: PropTypes.string,
  disabled: PropTypes.bool,
  multiSelect: PropTypes.bool,
  tipoEquipamento: PropTypes.string.isRequired,
  filterUsuarioUltimoRepasse: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
  showUsuarioTodosRepasse: PropTypes.oneOfType([PropTypes.string, PropTypes.bool]),
};

EquipamentoSelectV2.defaultProps = {
  error: false,
  isRequired: false,
  label: 'Equipamento',
  disabled: false,
  multiSelect: false,
  filterUsuarioUltimoRepasse: null,
  showUsuarioTodosRepasse: null,
};

export default EquipamentoSelectV2;
